import React, { startTransition } from 'react';

import UniversalRouter from 'universal-router';

import { Wifi } from '@travel/icons/service';
import { IS_GUEST_BOOKING_USER, TOKEN_KEY } from '@travel/traveler-core/constants/cookies';
import { extractProviderListURLQueries, toAppSearchObject } from '@travel/traveler-core/utils/seo';
import {
  getCookie,
  isEmptyArray,
  isNotEmptyValue,
  lazyWithSuspense,
  parseQueryString,
} from '@travel/utils';

import accountSettingsPageResolver from 'pages/AccountSettingsPage/resolver';
import areaPageResolver from 'pages/AreaPage/resolver';
import authRequestPageResolver from 'pages/AuthRequestPage/resolver';
import bookingStep1PageResolver from 'pages/BookingStep1/resolver';
import bookingStep2PageResolver from 'pages/BookingStep2/resolver';
import bookmarkListPageResolver from 'pages/BookmarkListPage/resolver';
import cancelStep1Resolver from 'pages/CancelStep1/resolver';
import couponDetailPageResolver from 'pages/CouponDetailPage/resolver';
import couponListPageResolver from 'pages/CouponListPage/resolver';
import emailSubscriptionManagementPageResolver from 'pages/EmailSubscriptionManagementPage/resolver';
import guestReviewDetailPage from 'pages/GuestReviewDetailPage/resolver';
import guestReviewListPage from 'pages/GuestReviewListPage/resolver';
import guideUserPageResolver from 'pages/GuideUserPage/resolver';
import legacyReservationDetailsPageResolver from 'pages/LegacyReservationDetails/resolver';
import legacyReservationListPageResolver from 'pages/LegacyReservationListPage/resolver';
import loginOrGuestPageResolver from 'pages/LoginOrGuestPage/resolver';
import modificationStep1Resolver from 'pages/ModificationStep1/resolver';
import modificationStep2Resolver from 'pages/ModificationStep2/resolver';
import modificationStep3Resolver from 'pages/ModificationStep3/resolver';
import nonMemberPageResolver from 'pages/NonMember/resolver';
import notificationDetailsResolver from 'pages/NotificationDetails/resolver';
import notificationsPageResolver from 'pages/NotificationsPage/resolver';
import postReviewPageResolver from 'pages/PostReviewPage/resolver';
import privacyPolicyPageResolver from 'pages/PrivacyPolicyPage/resolver';
import providerGuestReviewPageResolver from 'pages/ProviderGuestReviewPage/resolver';
import providerInformationPageResolver from 'pages/ProviderInformationPage/resolver';
import type { SearchProps as ProviderListPageSearchProps } from 'pages/ProviderListPage/resolver';
import providerListPageResolver from 'pages/ProviderListPage/resolver';
import reservationDetailsPageResolver from 'pages/ReservationDetails/resolver';
import reservationListPageResolver from 'pages/ReservationListPage/resolver';
import SkeletonPage from 'pages/SkeletonPage';
import topPageResolver from 'pages/TopPage/resolver';

import { pushLocation, setPreviousPageName } from 'store/__router/actions';
import { guestLogout, setIsGuestBookingUser } from 'store/authCode/actions';
import { updateMapExpansionState } from 'store/providerList/actions';
import { fetchSuggestions } from 'store/suggestions/actions';
import { getItems } from 'store/suggestions/selectors';

import type { NewQuery as BS1Query } from 'BookingStep-Types';
import { autoFilledCheckInDate, autoFilledCheckOutDate } from 'constants/date';
import { RAT_PAGE_TYPE_OBJ } from 'constants/ratAnalytics';
import App from 'core/App';
import appResolver, { appResolverMember } from 'core/App/resolver';
import paths, { baseUrl } from 'core/universalRouter/paths';
import type { MarketItem } from 'Markets-Types';

import { getIsWithInCampaignPeriod } from '../../store/featureConfig/selectors';
import type { universalRouterProps } from './types';

// Empty load options - fallback handled in lazyWithSuspense HOC
const loadOptions = {
  fallback: <SkeletonPage />,
};

// Hoisted lazy components for React 18 streaming SSR compatibility
// All lazy components must be declared at module level, not inside action functions

const LoginRedirection = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-LoginRedirection" */ 'pages/LoginRedirection'),
  loadOptions,
);

const AreaPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-AreaPage" */ 'pages/AreaPage'),
  loadOptions,
);

const ProviderListPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-ProviderListPage" */ 'pages/ProviderListPage'),
  loadOptions,
);

const PostReviewPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-PostReviewPage" */ 'pages/PostReviewPage'),
  loadOptions,
);

const NonMember = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-NonMember" */ 'pages/NonMember'),
  loadOptions,
);

const AuthRequestPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-AuthRequestPage" */ 'pages/AuthRequestPage'),
  loadOptions,
);

const AuthSentPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-AuthSentPage" */ 'pages/AuthSentPage'),
  loadOptions,
);

const GuideUserPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-GuideUserPage" */ 'pages/GuideUserPage'),
  loadOptions,
);

const BookingStepWrapper = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-BookingStep" */ 'pages/BookingStep'),
  loadOptions,
);

const BookingStep1 = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-BookingStep1" */ 'pages/BookingStep1'),
  loadOptions,
);

const BookingStep2 = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-BookingStep2" */ 'pages/BookingStep2'),
  loadOptions,
);

const BookingStep3 = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-BookingStep3" */ 'pages/BookingStep3'),
  loadOptions,
);

const CancelStep1 = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-CancelStep1" */ 'pages/CancelStep1'),
  loadOptions,
);

const CancelStep2 = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-CancelStep2" */ 'pages/CancelStep2'),
  loadOptions,
);

const GuestEntrance = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-GuestEntrance" */ 'pages/GuestEntrance'),
  loadOptions,
);

const CouponListPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-CouponListPage" */ 'pages/CouponListPage'),
  loadOptions,
);

const CouponDetailPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-CouponDetailPage" */ 'pages/CouponDetailPage'),
  loadOptions,
);

const BookmarkListPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-BookmarkListPage" */ 'pages/BookmarkListPage'),
  loadOptions,
);

const NotificationListPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-NotificationsPage" */ 'pages/NotificationsPage'),
  loadOptions,
);

const LogoutPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-LogoutPage" */ 'pages/LogoutPage'),
  loadOptions,
);

const ErrorPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-ErrorPage" */ 'pages/ErrorPage'),
  loadOptions,
);

const LoginOrGuestPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-LoginOrGuestPage" */ 'pages/LoginOrGuestPage'),
  loadOptions,
);

const LoginPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-LoginPage" */ 'pages/LoginPage'),
  loadOptions,
);

const PrivacyPolicyPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-PrivacyPolicyPage" */ 'pages/PrivacyPolicyPage'),
  loadOptions,
);

const OfflinePage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-OfflinePage" */ 'pages/OfflinePage'),
  loadOptions,
);

const ESimCampaign = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-ESimCampaign" */ 'pages/ESimCampaign'),
  loadOptions,
);

const NotFoundPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-NotFoundPage" */ 'pages/NotFoundPage'),
  loadOptions,
);

const TopPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-TopPage" */ 'pages/TopPage'),
  loadOptions,
);

const ProviderInformationPage = lazyWithSuspense(
  () =>
    import(/* webpackChunkName: "pages-ProviderInformationPage" */ 'pages/ProviderInformationPage'),
  loadOptions,
);

const ProviderGuestReviewPage = lazyWithSuspense(
  () =>
    import(/* webpackChunkName: "pages-ProviderGuestReviewPage" */ 'pages/ProviderGuestReviewPage'),
  loadOptions,
);

const PostReviewStep2Page = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-PostReviewStep2Page" */ 'pages/PostReviewStep2Page'),
  loadOptions,
);

const GuestReviewListPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-GuestReviewListPage" */ 'pages/GuestReviewListPage'),
  loadOptions,
);

const GuestReviewDetailPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-GuestReviewDetailPage" */ 'pages/GuestReviewDetailPage'),
  loadOptions,
);

const AuthMailFailPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-AuthMailFailPage" */ 'pages/AuthMailFailPage'),
  loadOptions,
);

const ReservationEntrancePage = lazyWithSuspense(
  () =>
    import(/* webpackChunkName: "pages-ReservationEntrancePage" */ 'pages/ReservationEntrancePage'),
  loadOptions,
);

const LegacyReservationEntrancePage = lazyWithSuspense(
  () =>
    import(
      /* webpackChunkName: "pages-LegacyReservationEntrancePage" */ 'pages/LegacyReservationEntrancePage'
    ),
  loadOptions,
);

const LegacyCancelStep2 = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-LegacyCancelStep2" */ 'pages/LegacyCancelStep2'),
  loadOptions,
);

const ModificationStep1 = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-ModificationStep1" */ 'pages/ModificationStep1'),
  loadOptions,
);

const ModificationStep2 = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-ModificationStep2" */ 'pages/ModificationStep2'),
  loadOptions,
);

// ModificationStep3 uses ssr: false option
const loadOptionsNoSSR = { ssr: false };

const ModificationStep3 = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-ModificationStep3" */ 'pages/ModificationStep3'),
  loadOptionsNoSSR,
);

const ReservationListPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-ReservationListPage" */ 'pages/ReservationListPage'),
  loadOptions,
);

const LegacyReservationListPage = lazyWithSuspense(
  () =>
    import(
      /* webpackChunkName: "pages-LegacyReservationListPage" */ 'pages/LegacyReservationListPage'
    ),
  loadOptions,
);

const ReservationDetails = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-ReservationDetails" */ 'pages/ReservationDetails'),
  loadOptions,
);

const LegacyReservationDetails = lazyWithSuspense(
  () =>
    import(
      /* webpackChunkName: "pages-LegacyReservationDetails" */ 'pages/LegacyReservationDetails'
    ),
  loadOptions,
);

const NotificationDetails = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-NotificationDetails" */ 'pages/NotificationDetails'),
  loadOptions,
);

const AccountSettingsPage = lazyWithSuspense(
  () => import(/* webpackChunkName: "pages-AccountSettingsPage" */ 'pages/AccountSettingsPage'),
  loadOptions,
);

const EmailSubscriptionManagementPage = lazyWithSuspense(
  () =>
    import(
      /* webpackChunkName: "pages-EmailSubscriptionManagementPage" */ 'pages/EmailSubscriptionManagementPage'
    ),
  loadOptions,
);

type RecursiveObject = {
  [key: string]: RecursiveObject | any;
};

export const getLimitations = (store: RecursiveObject) => {
  const marketItems = store?.markets?.items;
  const currentMarket = store?._i18n?.market;

  const currentMarketItem = marketItems?.find(
    (market: MarketItem) => market.marketCode === currentMarket.marketCode.toUpperCase(),
  );
  return currentMarketItem?.limitations;
};

export default (props: universalRouterProps) => {
  const isServer = props.isServer || false;
  const store = props.store.getState();

  const limitations = getLimitations(store);

  const isWithInCampaignPeriod = getIsWithInCampaignPeriod(store);

  const routes = [
    {
      path: paths.auth.path,
      chunkName: 'pages-LoginRedirection',
      async action(context: any) {
        if (!context.query) {
          props.res?.redirect(paths.top.pathResolver());
          return undefined;
        }

        return <LoginRedirection {...context} />;
      },
    },
    {
      path: '/',
      async action(context: any) {
        if (isServer) {
          // On server: fetch member on server entry
          await appResolverMember(props);
        } else if (!context.skipResolver) {
          // On client: fetch member and notification on client entry
          appResolver(props);
        }

        let children;
        try {
          children = await context.next();
        } catch (e) {
          console.error(e);
          children = null;
        }

        return (
          <App {...context} key="app">
            {children}
          </App>
        );
      },
      children: [
        {
          path: paths.top.path,
          chunkName: 'pages-TopPage',
          pageName: RAT_PAGE_TYPE_OBJ.top.parentName,
          async action(context: any) {
            const query = toAppSearchObject(context.query, limitations);

            if (isServer) {
              // to support open search
              if (context.query?.q) {
                try {
                  const store = props.store.getState();
                  await props.store.dispatch(fetchSuggestions(context.query?.q));
                  const suggestions = getItems(store);
                  const targetPlace = suggestions[0];

                  const pathResolver =
                    targetPlace.category === 'PROVIDER'
                      ? paths.providerInfo.pathResolver
                      : targetPlace.type === 'AREA' && targetPlace.category === 'SUBDIVISION'
                        ? paths.area.pathResolver
                        : paths.providerList.pathResolver;

                  props.res?.redirect(pathResolver(suggestions[0].pathId));
                  return undefined;
                } catch (error) {
                  // do nothing
                }
              }

              await topPageResolver(props);
            } else if (!context.skipResolver) {
              topPageResolver(props);
            }

            return <TopPage {...context} query={query} />;
          },
        },
        {
          path: paths.area.path,
          chunkName: 'pages-AreaPage',
          pageName: RAT_PAGE_TYPE_OBJ.area.parentName,
          async action(context: any) {
            const query = toAppSearchObject(context.query, limitations);

            const search = {
              params: { pathId: context.params.pathId },
              query,
            };

            if (isServer) {
              await areaPageResolver(props, search);
            } else if (!context.skipResolver) {
              areaPageResolver(props, search);
            }

            return <AreaPage {...context} query={query} />;
          },
        },
        {
          path: paths.providerList.path,
          chunkName: 'pages-ProviderListPage',
          isProviderListPage: true,
          async action(context: any) {
            const query = toAppSearchObject(context.query, limitations);
            const seoQuery = extractProviderListURLQueries(
              context.params?.features,
              context.params?.sortkey,
              context.params?.page,
            );

            const search: ProviderListPageSearchProps = {
              params: context.params,
              query: { ...query, ...seoQuery },
            };

            const isDatedSearch =
              query?.startDate ||
              query?.endDate ||
              autoFilledCheckInDate() ||
              autoFilledCheckOutDate();

            if (!context.skipResolver) {
              startTransition(() => {
                props.store.dispatch(
                  setPreviousPageName(
                    isDatedSearch
                      ? RAT_PAGE_TYPE_OBJ.providerListDated.parentName
                      : RAT_PAGE_TYPE_OBJ.providerListUndated.parentName,
                  ),
                );
              });
            }

            if (isServer) {
              await providerListPageResolver(props, search);
            } else if (!context.skipResolver) {
              providerListPageResolver(props, search);
            }

            return <ProviderListPage {...context} query={search.query} />;
          },
        },
        {
          path: paths.providerInfo.path,
          chunkName: 'pages-ProviderInformationPage',
          async action(context: any) {
            const query = toAppSearchObject(context.query, limitations);

            const search = {
              params: context.params,
              query,
            };
            const isDatedSearch =
              query?.startDate ||
              query?.endDate ||
              autoFilledCheckInDate() ||
              autoFilledCheckOutDate();

            if (!context.skipResolver) {
              startTransition(() => {
                props.store.dispatch(
                  setPreviousPageName(
                    isDatedSearch
                      ? RAT_PAGE_TYPE_OBJ.providerInfoDated.parentName
                      : RAT_PAGE_TYPE_OBJ.providerInfoUndated.parentName,
                  ),
                );
              });
            }

            if (isServer) {
              await providerInformationPageResolver(props, search);
            } else if (!context.skipResolver) {
              providerInformationPageResolver(props, search);
            }

            return <ProviderInformationPage {...context} query={query} shouldLazyLoad={true} />;
          },
        },
        {
          path: paths.providerReviewInfo.path,
          chunkName: 'pages-ProviderGuestReviewPage',
          pageName: RAT_PAGE_TYPE_OBJ.reviewList.parentName,
          async action(context: any) {
            const search = { params: context.params };

            if (isServer) {
              await providerGuestReviewPageResolver(props, search);
            } else if (!context.skipResolver) {
              await providerGuestReviewPageResolver(props, search);
            }

            return <ProviderGuestReviewPage {...context} />;
          },
        },
        {
          path: paths.guestReviewComplete.path,
          chunkName: 'pages-PostReviewStep2Page',
          isNotAllowedFirstLanding: true,
          isReservationManagementPage: true,
          async action(context: any) {
            return <PostReviewStep2Page {...context} />;
          },
        },
        {
          path: paths.guestReviewPost.path,
          chunkName: 'pages-PostReviewPage',
          pageName: RAT_PAGE_TYPE_OBJ.postReview.parentName,
          isReservationManagementPage: true,
          async action(context: any) {
            const search = { params: context.params };

            if (isServer) {
              await postReviewPageResolver(props, search);
            } else if (!context.skipResolver) {
              await postReviewPageResolver(props, search);
            }

            return <PostReviewPage {...context} />;
          },
        },
        {
          path: paths.guestReviewList.path,
          chunkName: 'pages-GuestReviewListPage',
          isReservationManagementPage: true,
          pageName: RAT_PAGE_TYPE_OBJ.guestReviewList.parentName,
          async action(context: any) {
            const search = { params: context.params, query: context.query };

            if (isServer) {
              await guestReviewListPage(props, search);
            } else if (!context.skipResolver) {
              guestReviewListPage(props, search);
            }

            return <GuestReviewListPage {...context} />;
          },
        },
        {
          path: paths.guestReviewDetail.path,
          chunkName: 'pages-GuestReviewDetailPage',
          isReservationManagementPage: true,
          pageName: RAT_PAGE_TYPE_OBJ.guestReviewDetail.parentName,
          async action(context: any) {
            const search = { params: context.params, query: context.query };

            if (isServer) {
              await guestReviewDetailPage(props, search);
            } else if (!context.skipResolver) {
              guestReviewDetailPage(props, search);
            }

            return <GuestReviewDetailPage {...context} />;
          },
        },
        {
          path: paths.guestBooking.path,
          chunkName: 'pages-NonMember',
          async action(context: { params: { toWhere: string; deviceId: string; token: string } }) {
            const { toWhere, deviceId } = context.params;
            try {
              const res = (await nonMemberPageResolver(props, context)) as {
                token?: string;
              };
              if (res && res.token) {
                return (
                  <NonMember toWhere={toWhere} deviceId={deviceId} token={res.token} {...context} />
                );
              }
              //@ts-ignore
            } catch ({ commonErrorHandler, status }) {
              if (status === 401) {
                const callback = Buffer.from(toWhere, 'base64').toString();
                return <AuthMailFailPage {...context} callback={callback} />;
              } else {
                commonErrorHandler();
              }
            }
          },
        },
        {
          path: baseUrl.booking,
          children: [
            {
              path: paths.reservationForm.subPath,
              chunkName: 'pages-ReservationEntrancePage',
              async action(context: any) {
                return <ReservationEntrancePage {...context} query={context.query} />;
              },
            },
            {
              path: paths.legacyReservationForm.subPath,
              chunkName: 'pages-LegacyReservationEntrancePage',
              async action(context: any) {
                return <LegacyReservationEntrancePage {...context} query={context.query} />;
              },
            },
            {
              path: paths.bookingMailRequest.subPath,
              chunkName: 'pages-AuthRequestPage',
              isGuestOnly: true,
              pageName: RAT_PAGE_TYPE_OBJ.authRequest.parentName,
              async action(context: any) {
                const query = toAppSearchObject(context.query, limitations);

                if (isServer) {
                  await authRequestPageResolver(props, query);
                } else if (!context.skipResolver) {
                  authRequestPageResolver(props, query);
                }

                return <AuthRequestPage {...context} />;
              },
            },
            {
              path: paths.bookingMailAccept.subPath,
              chunkName: 'pages-AuthSentPage',
              isNotAllowedFirstLanding: true,
              async action(context: any) {
                return <AuthSentPage {...context} />;
              },
            },
            {
              path: paths.bookingMailFail.subPath,
              chunkName: 'pages-AuthMailFailPage',
              isNotAllowedFirstLanding: true,
              async action(context: any) {
                return <AuthMailFailPage {...context} />;
              },
            },
            {
              path: paths.guideUser.subPath,
              chunkName: 'pages-GuideUserPage',
              async action(context: any) {
                const query = toAppSearchObject(context.query, limitations);

                const search = {
                  params: context.params,
                  query,
                };

                if (isServer) {
                  await guideUserPageResolver(props, search);
                } else if (!context.skipResolver) {
                  guideUserPageResolver(props, search);
                }

                return <GuideUserPage {...context} query={query} />;
              },
            },
            {
              path: '/',
              async action(context: any) {
                const children = await context.next();
                return (
                  <BookingStepWrapper {...context} key="bookingSteps">
                    {children}
                  </BookingStepWrapper>
                );
              },
              children: [
                {
                  path: paths.bookingStep1.subPath,
                  chunkName: 'pages-BookingStep1',
                  isReservationManagementPage: true,
                  pageName: RAT_PAGE_TYPE_OBJ.bookingStep1.parentName,
                  async action(context: any) {
                    const query = toAppSearchObject(context.query, limitations) as BS1Query;
                    const search = { params: context.params, query };

                    if (isServer) {
                      await bookingStep1PageResolver(props, search);
                    } else if (!context.skipResolver) {
                      await bookingStep1PageResolver(props, search);
                    }
                    return <BookingStep1 {...context} />;
                  },
                },
                {
                  path: paths.bookingStep2.subPath,
                  chunkName: 'pages-BookingStep2',
                  isReservationManagementPage: true,
                  pageName: RAT_PAGE_TYPE_OBJ.bookingStep2.parentName,
                  async action(context: any) {
                    if (isServer) {
                      await bookingStep2PageResolver(props, context.query.stepId);
                    } else if (!context.skipResolver) {
                      await bookingStep2PageResolver(props, context.query.stepId);
                    }

                    return <BookingStep2 {...context} />;
                  },
                },
                {
                  path: paths.bookingStep3.subPath,
                  chunkName: 'pages-BookingStep3',
                  isReservationManagementPage: true,
                  isNotAllowedFirstLanding: true,
                  pageName: RAT_PAGE_TYPE_OBJ.bookingStep3.parentName,
                  async action(context: any) {
                    return <BookingStep3 {...context} />;
                  },
                },
              ],
            },
          ],
        },
        {
          path: baseUrl.cancellation,
          children: [
            {
              path: paths.cancellationStep1.subPath,
              chunkName: 'pages-CancelStep1',
              isReservationManagementPage: true,
              pageName: RAT_PAGE_TYPE_OBJ.cancelStep1.parentName,
              async action(context: any) {
                const search = { params: context.params, query: context.query };

                if (isServer) {
                  await cancelStep1Resolver(props, search);
                } else if (!context.skipResolver) {
                  cancelStep1Resolver(props, search);
                }
                return <CancelStep1 {...context} />;
              },
            },
            {
              path: paths.cancellationStep2.subPath,
              chunkName: 'pages-CancelStep2',
              isNotAllowedFirstLanding: true,
              isReservationManagementPage: true,
              async action(context: any) {
                return <CancelStep2 {...context} />;
              },
            },
            {
              path: paths.legacyCancellationStep2.subPath,
              chunkName: 'pages-LegacyCancelStep2',
              isNotAllowedFirstLanding: true,
              async action(context: any) {
                return <LegacyCancelStep2 {...context} />;
              },
            },
          ],
        },
        {
          path: baseUrl.modification,
          children: [
            {
              path: paths.modificationStep1.subPath,
              chunkName: 'pages-ModificationStep1',
              isReservationManagementPage: true,
              pageName: RAT_PAGE_TYPE_OBJ.modification.parentName,
              async action(context: any) {
                const search = { params: context.params };

                if (isServer) {
                  await modificationStep1Resolver(props, search);
                } else if (!context.skipResolver) {
                  modificationStep1Resolver(props, search);
                }

                return <ModificationStep1 {...context} />;
              },
            },
            {
              path: paths.modificationStep2.subPath,
              chunkName: 'pages-ModificationStep2',
              isReservationManagementPage: true,
              pageName: RAT_PAGE_TYPE_OBJ.modificationStep2.parentName,
              async action(context: any) {
                const search = { params: context.params, query: context.query };

                if (isServer) {
                  await modificationStep2Resolver(props, search);
                } else if (!context.skipResolver) {
                  modificationStep2Resolver(props, search);
                }

                return <ModificationStep2 {...context} />;
              },
            },
            {
              path: paths.modificationStep3.subPath,
              chunkName: 'pages-ModificationStep3',
              isNotAllowedFirstLanding: true,
              isReservationManagementPage: true,
              pageName: RAT_PAGE_TYPE_OBJ.modificationStep3.parentName,
              async action(context: any) {
                const search = { params: context.params };

                if (isServer) {
                  await modificationStep3Resolver(props, search);
                } else if (!context.skipResolver) {
                  await modificationStep3Resolver(props, search);
                }

                return <ModificationStep3 {...context} />;
              },
            },
          ],
        },
        {
          path: baseUrl.reservation,
          children: [
            {
              path: paths.reservationList.subPath,
              chunkName: 'pages-ReservationListPage',
              isReservationManagementPage: true,
              async action(context: any) {
                const store = props.store.getState();
                const hasToken = store.authCode.item.token;
                // if there's token assume it's from reservation entry.
                if (!store.member.isMember && !hasToken) {
                  props.store.dispatch(pushLocation(paths.top.pathResolver()));
                  return;
                }

                if (isServer) {
                  await reservationListPageResolver(props, context.query);
                } else if (!context.skipResolver) {
                  reservationListPageResolver(props, context.query);
                }
                return <ReservationListPage {...context} />;
              },
            },
            {
              path: paths.legacyReservationList.subPath,
              chunkName: 'pages-LegacyReservationListPage',
              isReservationManagementPage: true,
              async action(context: any) {
                const store = props.store.getState();
                const hasToken = store.authCode.item.token;
                // if there's token assume it's from reservation entry.
                if (!store.member.isMember && !hasToken) {
                  props.store.dispatch(pushLocation(paths.top.pathResolver()));
                  return;
                }

                if (isServer) {
                  await legacyReservationListPageResolver(props, context.query);
                } else if (!context.skipResolver) {
                  legacyReservationListPageResolver(props, context.query);
                }
                return <LegacyReservationListPage {...context} />;
              },
            },
            {
              path: paths.reservationDetail.subPath,
              chunkName: 'pages-ReservationDetails',
              isReservationManagementPage: true,
              pageName: 'reservation_details',
              async action(context: any) {
                const search = { params: context.params };

                if (isServer) {
                  await reservationDetailsPageResolver(props, search);
                } else if (!context.skipResolver) {
                  await reservationDetailsPageResolver(props, search);
                }
                return <ReservationDetails {...context} />;
              },
            },
            {
              path: paths.legacyReservationDetail.subPath,
              chunkName: 'pages-LegacyReservationDetails',
              pageName: 'reservation_details',
              async action(context: any) {
                const query = toAppSearchObject(context.query, limitations) as { email: string };
                const search = { params: context.params, query };

                if (isServer) {
                  await legacyReservationDetailsPageResolver(props, search);
                } else if (!context.skipResolver) {
                  legacyReservationDetailsPageResolver(props, search);
                }
                return <LegacyReservationDetails {...search} />;
              },
            },
            {
              path: paths.reservationLogin.subPath,
              chunkName: 'pages-GuestEntrance',
              async action(context: any) {
                const store = props.store.getState();
                const hasToken = store.authCode.item.token;
                if (store.member.isMember || hasToken) {
                  props.store.dispatch(pushLocation(paths.reservationList.path));
                  return;
                }

                return <GuestEntrance />;
              },
            },
          ],
        },
        {
          path: paths.couponList.path,
          chunkName: 'pages-CouponListPage',
          pageName: RAT_PAGE_TYPE_OBJ.couponSearch.parentName,
          async action(context: any) {
            const search = { query: { offset: context.offset, limit: context.limit } };

            if (isServer) {
              await couponListPageResolver(props, search);
            } else if (!context.skipResolver) {
              couponListPageResolver(props, search);
            }
            return <CouponListPage {...context} />;
          },
        },
        {
          path: paths.couponDetail.path,
          chunkName: 'pages-CouponDetailPage',
          pageName: RAT_PAGE_TYPE_OBJ.couponDetail.parentName,
          async action(context: any) {
            const search = { params: context.params };

            if (isServer) {
              await couponDetailPageResolver(props, search);
            } else if (!context.skipResolver) {
              couponDetailPageResolver(props, search);
            }
            return <CouponDetailPage {...context} />;
          },
        },
        {
          path: paths.bookmarkList.path,
          chunkName: 'pages-BookmarkListPage',
          pageName: RAT_PAGE_TYPE_OBJ.bookmarkList.parentName,
          async action(context: any) {
            const search = { query: { offset: context.offset, limit: context.limit } };

            if (isServer) {
              await bookmarkListPageResolver(props, search);
            } else if (!context.skipResolver) {
              bookmarkListPageResolver(props, search);
            }
            return <BookmarkListPage {...context} />;
          },
        },
        {
          path: paths.notificationList.path,
          chunkName: 'pages-NotificationsPage',
          pageName: RAT_PAGE_TYPE_OBJ.notifications.parentName,
          async action(context: any) {
            if (isServer) {
              await notificationsPageResolver(props);
            } else if (!context.skipResolver) {
              notificationsPageResolver(props);
            }
            return <NotificationListPage {...context} />;
          },
        },
        {
          path: paths.notificationDetail.path,
          chunkName: 'pages-NotificationDetails',
          pageName: RAT_PAGE_TYPE_OBJ.notificationDetails.parentName,
          async action(context: any) {
            const search = { params: context.params };
            if (isServer) {
              await notificationDetailsResolver(props, search);
            } else if (!context.skipResolver) {
              notificationDetailsResolver(props, search);
            }
            return <NotificationDetails {...context} />;
          },
        },
        {
          path: paths.logout.path,
          chunkName: 'pages-LogoutPage',
          isNotAllowedFirstLanding: true,
          async action(context: any) {
            return <LogoutPage {...context} />;
          },
        },
        {
          path: paths.loginError.path,
          chunkName: 'pages-ErrorPage',
          isNotAllowedFirstLanding: true,
          async action(context: any) {
            return (
              <ErrorPage
                icon={<Wifi />}
                mainText="Sorry but there is a problem happens to our site, please visit us later."
                {...context}
              />
            );
          },
        },
        {
          path: paths.loginOrGuest.path,
          chunkName: 'pages-LoginOrGuestPage',
          isGuestOnly: true,
          pageName: RAT_PAGE_TYPE_OBJ.loginOrGuest.parentName,
          async action(context: any) {
            const query = toAppSearchObject(context.query, limitations);

            if (isServer) {
              await loginOrGuestPageResolver(props, query);
            } else if (!context.skipResolver) {
              loginOrGuestPageResolver(props, query);
            }

            return <LoginOrGuestPage {...context} />;
          },
        },
        {
          path: paths.accountSetting.path,
          chunkName: 'pages-AccountSettingsPage',
          isMemberOnly: true,
          async action(context: any) {
            if (isServer) {
              await accountSettingsPageResolver(props);
            } else if (!context.skipResolver) {
              accountSettingsPageResolver(props);
            }

            return <AccountSettingsPage {...context} />;
          },
        },
        {
          path: paths.emailSubscriptionManagement.path,
          chunkName: 'pages-EmailSubscriptionManagementPage',
          pageName: RAT_PAGE_TYPE_OBJ.mailSubscription.parentName,
          async action(context: any) {
            if (isServer) {
              await emailSubscriptionManagementPageResolver(props);
            } else if (!context.skipResolver) {
              emailSubscriptionManagementPageResolver(props);
            }

            return <EmailSubscriptionManagementPage {...context} />;
          },
        },
        {
          path: paths.signIn.path,
          chunkName: 'pages-LoginPage',
          async action(context: any) {
            return <LoginPage {...context} />;
          },
        },
        {
          path: paths.privacyPolicy.path,
          chunkName: 'pages-PrivacyPolicyPage',
          async action(context: any) {
            if (isServer) {
              await privacyPolicyPageResolver(props, context);
            } else if (!context.skipResolver) {
              await privacyPolicyPageResolver(props, context);
            }

            return <PrivacyPolicyPage />;
          },
        },
        {
          path: paths.offline.path,
          chunkName: 'pages-OfflinePage',
          isNotAllowedFirstLanding: true,
          action() {
            return <OfflinePage />;
          },
        },
        ...(isWithInCampaignPeriod
          ? [
              {
                path: paths.eSimCampaign.path,
                chunkName: 'pages-ESimCampaign',
                async action(context: any) {
                  const query = toAppSearchObject(context.query, limitations);

                  return <ESimCampaign {...context} query={query} />;
                },
              },
            ]
          : []),
        {
          // also resolve paths.notFound.path
          path: '(.*)',
          chunkName: 'pages-NotFoundPage',
          async action() {
            return <NotFoundPage />;
          },
        },
      ],
    },
  ];
  return new UniversalRouter(routes, {
    resolveRoute(context, params) {
      const currentRoute = context.route as {
        path: string;
        pageName: string;
        isProviderListPage: boolean;
        isReservationManagementPage?: boolean;
        isMemberOnly: boolean;
        isGuestOnly: boolean;
        isNotAllowedFirstLanding?: boolean;
        chunkName?: string;
      } & typeof context.route;
      const isClient = typeof window !== 'undefined';

      if (isNotEmptyValue(currentRoute.pageName) && !context.skipResolver) {
        startTransition(() => {
          props.store?.dispatch(setPreviousPageName(currentRoute.pageName));
        });
      }

      if (isServer && currentRoute.isNotAllowedFirstLanding) {
        props.store?.dispatch(pushLocation(paths.top.pathResolver()));
        return undefined;
      }
      const store = props.store.getState();
      const tokenPrefix = `${TOKEN_KEY}=@`;
      const isNotLogin =
        (isServer && !props.req?.headers?.cookie?.includes(tokenPrefix)) ||
        (isClient && !store.authCode?.item?.token); // SSR || CSR

      const { router } = props.store?.getState();
      const {
        location: { location },
        baseUrl,
      } = router;

      // We need to close the map dialog when we are not on providerListPage it stays open on rout changes and back button click etc
      if (
        currentRoute.path?.length > 1 &&
        !currentRoute.isProviderListPage &&
        !context.skipResolver
      ) {
        startTransition(() => {
          props.store?.dispatch(updateMapExpansionState(false));
        });
      }

      if (currentRoute.isMemberOnly && isNotLogin) {
        const callbackUrl = baseUrl + location.pathname + location.search;
        props.store?.dispatch(pushLocation(`${paths.signIn.path}?target_url=${callbackUrl}`));
        return undefined;
      }

      if (currentRoute.isGuestOnly && !isNotLogin) {
        // directly navigate user to callbackUrl(BS1) when user is member
        const queryString = parseQueryString(context.search);
        const url = queryString?.callback?.split(baseUrl)?.[1];
        if (url) {
          props.store?.dispatch(pushLocation(url));
        } else {
          props.store?.dispatch(pushLocation(paths.top.pathResolver()));
        }
        return undefined;
      }

      if (currentRoute.isReservationManagementPage) {
        if (isNotLogin) {
          return <GuestEntrance />;
        }
      } else if (isClient && isEmptyArray(currentRoute.children)) {
        const {
          authCode: { isGuestBookingUser },
        } = props.store?.getState();
        const isGuestBooking = getCookie(IS_GUEST_BOOKING_USER);

        if (isGuestBookingUser || isGuestBooking) {
          // reset guest booking state when the user is navigated to other pages instead of reservation management pages
          props.store?.dispatch(setIsGuestBookingUser(false));
          props.store?.dispatch(guestLogout());
        }
      }

      if (typeof currentRoute.action === 'function') {
        if (context.search) {
          context.query = parseQueryString(context.search);
        }

        if (props.initialChunks && currentRoute.chunkName) {
          props.initialChunks.push(currentRoute.chunkName);
        }

        return context.route.action && context.route.action(context, params);
      }
      return undefined;
    },
  });
};
